import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useMediaQuery } from "@mui/material";
import api from "../../api";
import Can from "../../components/Can";
import ConfirmModal from "../../components/common/ConfirmModal";
import { usePermissions } from "../../context/PermissionContext";
import { compactPageItems } from "../../components/ui/compactPagination";
import { getSerialNumber } from "../../utils/pagination";
import {
  filterDepartmentsByUnit,
  filterDesignations,
  filterReportingManagers,
  withSavedOption,
} from "../../utils/orgOptions";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import "../admins/AdminStaffPage.css";
import "../technicians/TechnicianDesignSystem.css";
import "./ManagerTeamUsersPage.css";
import { avatarTone, roleKey, Empty } from "./teamUserDisplay";

// Manager -> Team Users. Every call goes to /manager/users, which the
// backend scopes to the caller's own unit and checks users:*_team on
// (services/managerUserService.js). Button visibility below mirrors those
// permissions for UX only -- the backend is the real control.

const PAGE_SIZE = 10;

const emptyForm = {
  name: "",
  username: "",
  email: "",
  phone_no: "",
  password: "",
  role_id: "",
  employee_code: "",
  department_id: "",
  designation_id: "",
  reporting_manager_id: "",
  is_active: 1,
};

const ORG_FIELDS = ["department_id", "designation_id", "reporting_manager_id"];
const toId = (v) => (v ? Number(v) : null);

// A saved value that is not selectable any more renders disabled.
const renderOrgOption = (o, label = o.name) => (
  <option key={o.id} value={o.id} disabled={!!o.saved}>
    {o.saved ? `${label} (saved, not selectable)` : label}
  </option>
);

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

const ManagerTeamUsersPage = () => {
  const { can } = usePermissions();
  const canCreate = can("users", "create_team");
  const canUpdate = can("users", "update_team");
  const canDeactivate = can("users", "delete_team");
  // Same 767px phone breakpoint as the Technician pages (TechnicianDesignSystem.css).
  // Without matchMedia (tests, SSR) this is false, so the table renders.
  const isMobile = useMediaQuery("(max-width:767px)");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // unit = the manager's own unit from the backend (read-only); options are
  // already scoped to it server-side.
  const [options, setOptions] = useState({ unit: null, roles: [], departments: [], designations: [], reportingManagers: [] });

  const [formMode, setFormMode] = useState(null); // "create" | "edit" | null
  const [editingId, setEditingId] = useState(null);
  // Editing their own record: role and status are fixed (the backend
  // rejects changing either), so the form shows the role read-only and
  // never sends role_id/is_active.
  const [editingSelf, setEditingSelf] = useState(null); // { role_name } | null
  const [form, setForm] = useState(emptyForm);
  // The edited user's saved org values (ids + names), so they stay visible
  // even when outside the selectable options (old cross-unit data).
  const [savedOrg, setSavedOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [viewUser, setViewUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  // Debounced search -> server query.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const res = await api.get(`/manager/users`, {
        params: {
          page,
          pageSize: PAGE_SIZE,
          search: search || undefined,
          is_active: statusFilter || undefined,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.meta?.total) || 0);
    } catch (err) {
      console.error("Fetch team users failed", err);
      const message = errorMessage(err, "Failed to load team users");
      setLoadError(message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!canCreate && !canUpdate) return;
    // Roles come from the same /roles list the admin panel uses; if the
    // caller isn't allowed to read it, fall back to the roles returned by the
    // options endpoint.
    const rolesReq = api
      .get(`/roles`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data;
        return Array.isArray(list) ? list : null;
      })
      .catch(() => null);

    Promise.all([api.get(`/manager/users/options`), rolesReq])
      .then(([res, allRoles]) => {
        const d = res.data?.data || {};
        setOptions({
          unit: d.unit || null,
          roles: d.roles || [],
          departments: d.departments || [],
          designations: d.designations || [],
          reportingManagers: d.reportingManagers || [],
        });
      })
      .catch((err) => console.error("Fetch team user options failed", err));
  }, [canCreate, canUpdate]);

  // Same unit-wise rules as the Admin form (utils/orgOptions), with the unit
  // fixed to the manager's own. The backend already scopes these lists and
  // re-validates every submitted id; this is a second, UX-only layer.
  const managerUnitId = options.unit?.id || null;
  const managerUnitName =
    options.unit?.name || rows.find((r) => r.unit_name)?.unit_name || "Your unit";

  const unitDepartments = useMemo(
    () => filterDepartmentsByUnit(options.departments, managerUnitId),
    [options.departments, managerUnitId]
  );

  const departmentOptions = useMemo(
    () => withSavedOption(unitDepartments, savedOrg?.department_id, savedOrg?.department_name),
    [unitDepartments, savedOrg]
  );

  const designationOptions = useMemo(() => {
    const allowed = new Set(unitDepartments.map((d) => String(d.id)));
    return withSavedOption(
      filterDesignations(options.designations, form.department_id, allowed),
      savedOrg?.designation_id,
      savedOrg?.designation_name
    );
  }, [options.designations, unitDepartments, form.department_id, savedOrg]);

  const reportingManagerOptions = useMemo(
    () =>
      withSavedOption(
        filterReportingManagers(options.reportingManagers, { unitId: managerUnitId, excludeId: editingId }),
        savedOrg?.reporting_manager_id,
        savedOrg?.reporting_manager_name
      ),
    [options.reportingManagers, managerUnitId, editingId, savedOrg]
  );

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openCreate = () => {
    setShowPassword(false);
    setForm(emptyForm);
    setSavedOrg(null);
    setEditingId(null);
    setEditingSelf(null);
    setFormMode("create");
  };

  const openEdit = (user) => {
    setForm({
      name: user.name || "",
      username: user.username || "",
      email: user.email || "",
      phone_no: user.phone_no || "",
      password: "",
      role_id: user.role_id || "",
      employee_code: user.employee_code || "",
      department_id: user.department_id || "",
      designation_id: user.designation_id || "",
      reporting_manager_id: user.reporting_manager_id || "",
      is_active: user.is_active ? 1 : 0,
    });
    setSavedOrg({
      department_id: user.department_id || "",
      department_name: user.department_name,
      designation_id: user.designation_id || "",
      designation_name: user.designation_name,
      reporting_manager_id: user.reporting_manager_id || "",
      reporting_manager_name: user.reporting_manager_name,
    });
    setEditingId(user.id);
    setEditingSelf(user.is_self ? { role_name: user.role_name } : null);
    setFormMode("edit");
  };

  const closeForm = () => {
    if (saving) return;
    setFormMode(null);
    setEditingId(null);
    setEditingSelf(null);
    setSavedOrg(null);
    setForm(emptyForm);
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (formMode === "create" && !form.username.trim()) return "Username is required";
    if (!form.email.trim()) return "Email is required";
    if (formMode === "create" && form.password.length < 6) return "Password must be at least 6 characters";
    if (!editingSelf && !form.role_id) return "Please select a role";
    return "";
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      toast.warning(problem);
      return;
    }

    // unit_id / company_id are never sent: the backend always uses the
    // manager's own unit.
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone_no: form.phone_no.trim(),
      employee_code: form.employee_code.trim() || null,
    };
    // On edit, an org field left at its saved value is not re-sent: a stale
    // saved value (e.g. old cross-unit data the backend now rejects) must
    // not block saving the user's other fields. Changed values are sent and
    // validated against the manager's unit by the backend.
    for (const key of ORG_FIELDS) {
      if (formMode === "edit" && savedOrg && toId(form[key]) === toId(savedOrg[key])) continue;
      payload[key] = toId(form[key]);
    }
    if (!editingSelf) payload.role_id = Number(form.role_id);

    try {
      setSaving(true);
      if (formMode === "create") {
        await api.post(`/manager/users`, { ...payload, username: form.username.trim(), password: form.password });
        toast.success("User created successfully");
      } else {
        await api.put(
          `/manager/users/${editingId}`,
          editingSelf ? payload : { ...payload, is_active: form.is_active ? 1 : 0 }
        );
        toast.success("User updated successfully");
      }
      setFormMode(null);
      setEditingId(null);
      setEditingSelf(null);
      setSavedOrg(null);
      setForm(emptyForm);
      fetchUsers();
    } catch (err) {
      console.error("Save team user failed", err);
      toast.error(errorMessage(err, "Failed to save user"));
    } finally {
      setSaving(false);
    }
  };

  const openView = async (user) => {
    setViewUser(user);
    setViewLoading(true);
    try {
      const res = await api.get(`/manager/users/${user.id}`);
      setViewUser(res.data?.data || user);
    } catch (err) {
      console.error("Fetch team user failed", err);
      toast.error(errorMessage(err, "Failed to load user details"));
      setViewUser(null);
    } finally {
      setViewLoading(false);
    }
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await api.delete(`/manager/users/${deactivateTarget.id}`);
      toast.success("User deactivated");
      setDeactivateTarget(null);
      fetchUsers();
    } catch (err) {
      console.error("Deactivate team user failed", err);
      toast.error(errorMessage(err, "Failed to deactivate user"));
    } finally {
      setDeactivating(false);
    }
  };

  const reactivate = async (user) => {
    try {
      await api.put(`/manager/users/${user.id}`, { is_active: 1 });
      toast.success("User activated");
      fetchUsers();
    } catch (err) {
      console.error("Activate team user failed", err);
      toast.error(errorMessage(err, "Failed to activate user"));
    }
  };

  const detailRows = viewUser
    ? [
        ["Name", viewUser.name],
        ["Username", viewUser.username],
        ["Email", viewUser.email],
        ["Phone", viewUser.phone_no],
        ["Employee Code", viewUser.employee_code],
        ["Role", viewUser.role_name],
        ["Unit", viewUser.unit_name],
        ["Company", viewUser.company_name],
        ["Department", viewUser.department_name],
        ["Designation", viewUser.designation_name],
        ["Reporting Manager", viewUser.reporting_manager_name],
        ["Status", viewUser.is_active ? "Active" : "Inactive"],
      ]
    : [];

  // Row actions, shared by the desktop table and the mobile cards so both
  // show exactly the same controls under the same permission checks.
  const renderActions = (user) => (
    <div className="unit-actions">
      <button
        type="button"
        className="unit-icon-btn tu-action-view"
        title="View User"
        aria-label={`View ${user.name || "user"}`}
        onClick={() => openView(user)}
      >
        <i className="fa-solid fa-eye" aria-hidden="true"></i>
      </button>

      {user.is_manageable !== false && (
        <Can moduleKey="users" actionKey="update_team">
          <button
            type="button"
            className="unit-icon-btn unit-edit staff-action-edit"
            title="Edit User"
            aria-label={`Edit ${user.name || "user"}`}
            onClick={() => openEdit(user)}
          >
            <i className="fa-solid fa-pen" aria-hidden="true"></i>
          </button>
        </Can>
      )}

      {/* Never on another Manager/Admin's row, never on the
          manager's own row -- the backend refuses both. */}
      {user.is_manageable === false || user.is_self ? null : user.is_active ? (
        <Can moduleKey="users" actionKey="delete_team">
          <button
            type="button"
            className="unit-icon-btn unit-delete staff-action-delete"
            title="Deactivate User"
            aria-label={`Deactivate ${user.name || "user"}`}
            onClick={() => setDeactivateTarget(user)}
          >
            <i className="fa-solid fa-user-slash" aria-hidden="true"></i>
          </button>
        </Can>
      ) : (
        <Can moduleKey="users" actionKey="update_team">
          <button
            type="button"
            className="unit-icon-btn staff-action-toggle staff-action-toggle--activate"
            title="Activate User"
            aria-label={`Activate ${user.name || "user"}`}
            onClick={() => reactivate(user)}
          >
            <i className="fa-solid fa-play" aria-hidden="true"></i>
          </button>
        </Can>
      )}
    </div>
  );

  const renderNameBlock = (user) => (
    <div className="unit-name-box">
      <div className={`unit-letter tu-avatar tu-avatar-${avatarTone(user.name)}`} aria-hidden="true">
        {user.name?.charAt(0)?.toUpperCase() || "U"}
      </div>
      <div className="tu-name-text">
        <strong>{user.name || <Empty />}</strong>
        {user.employee_code && (
          <small>
            <span className="visually-hidden">Employee code </span>
            {user.employee_code}
          </small>
        )}
      </div>
    </div>
  );

  const renderRole = (user) =>
    user.role_name ? (
      <span className={`tu-role tu-role-${roleKey(user.role_name)}`}>{user.role_name.replace(/_/g, " ")}</span>
    ) : (
      <Empty />
    );

  const renderStatus = (user) => (
    <span className={`tu-status ${user.is_active ? "tu-status-active" : "tu-status-inactive"}`}>
      {user.is_active ? "Active" : "Inactive"}
    </span>
  );

  return (
    <div className="unit-page admin-tasks-theme team-users-page team-users-manage tech-ds">
      <div className="unit-header tech-page-header">
        <div>
          <div className="unit-page-label tech-section-label">User Management</div>
          <h2 className="tech-page-title">Team Users</h2>
          <p className="tech-page-subtitle">View and manage the users in your unit.</p>
        </div>

        <div className="unit-header-actions tech-header-actions">
          <button type="button" className="unit-btn unit-btn-light" onClick={fetchUsers} disabled={loading}>
            <i className={`fa-solid fa-rotate${loading ? " fa-spin" : ""}`} aria-hidden="true"></i>
            {loading ? "Refreshing..." : "Refresh"}
          </button>

          <Can moduleKey="users" actionKey="create_team">
            <button type="button" className="unit-btn unit-btn-primary" onClick={openCreate}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              Add User
            </button>
          </Can>
        </div>
      </div>

      <div className="unit-card tech-card">
        <div className="unit-toolbar tech-toolbar">
          <div className="tech-toolbar-title">
            <h5>Team List</h5>
            <p>Only users in your own unit are shown.</p>
          </div>

          <div className="unit-toolbar-actions tech-filters">
            <div className="input-group unit-search tech-search">
              <span className="input-group-text" aria-hidden="true">
                <i className="fa-solid fa-magnifying-glass"></i>
              </span>
              <input
                className="form-control"
                placeholder="Search name, username, email, phone, employee code..."
                aria-label="Search team users"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <select
              className="form-select staff-filter tech-select tech-select--wide"
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">All Statuses</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="unit-loader">
            <div className="spinner-border" role="status"></div>
            <p>Loading team users...</p>
          </div>
        ) : loadError ? (
          <div className="unit-empty" role="alert">
            <div className="unit-empty-icon">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
            </div>
            <h5>Unable to load team users.</h5>
            <p>{loadError}</p>
            <button type="button" className="unit-btn unit-btn-light" onClick={fetchUsers}>
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="unit-empty">
            <div className="unit-empty-icon">
              <i className="fa-solid fa-users" aria-hidden="true"></i>
            </div>
            <h5>No team users found</h5>
            <p>{search || statusFilter ? "Try adjusting your search or status filter." : "There are no users in your unit yet."}</p>
          </div>
        ) : isMobile ? (
          // Phones: one card per user with every table field, same rows and
          // the same action buttons as the table.
          <ul className="tu-cards" aria-label="Team users">
            {rows.map((user, index) => (
              <li key={user.id} className={`tu-card${user.is_active ? "" : " tu-row-inactive"}`}>
                <div className="tu-card-head">
                  {renderNameBlock(user)}
                  {renderStatus(user)}
                </div>

                <dl className="tu-card-fields">
                  <div>
                    <dt>S.No.</dt>
                    <dd>{getSerialNumber(index, page, PAGE_SIZE)}</dd>
                  </div>
                  <div>
                    <dt>Username</dt>
                    <dd>{user.username || <Empty />}</dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>{user.email || <Empty />}</dd>
                  </div>
                  <div>
                    <dt>Phone</dt>
                    <dd>{user.phone_no || <Empty />}</dd>
                  </div>
                  <div>
                    <dt>Role</dt>
                    <dd>{renderRole(user)}</dd>
                  </div>
                  <div>
                    <dt>Department</dt>
                    <dd>{user.department_name || <Empty />}</dd>
                  </div>
                </dl>

                <div className="tu-card-actions">{renderActions(user)}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="unit-table-wrapper tech-table-wrap tu-scroll"
            role="region"
            aria-label="Team users table (scroll sideways to see all columns)"
            tabIndex={0}
          >
            <table className="table unit-table tu-table">
              <thead>
                <tr>
                  <th scope="col" className="tu-col-sno">S.NO.</th>
                  <th scope="col" className="tu-col-name">Name</th>
                  <th scope="col" className="tu-col-user">Username</th>
                  <th scope="col" className="tu-col-email">Email</th>
                  <th scope="col" className="tu-col-phone">Phone</th>
                  <th scope="col" className="tu-col-role">Role</th>
                  <th scope="col" className="tu-col-dept">Department</th>
                  <th scope="col" className="tu-col-status">Status</th>
                  <th scope="col" className="tu-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user, index) => (
                  <tr key={user.id} className={user.is_active ? "" : "tu-row-inactive"}>
                    <td className="tu-sno tu-col-sno">{getSerialNumber(index, page, PAGE_SIZE)}</td>
                    <td className="tu-col-name">{renderNameBlock(user)}</td>
                    <td className="tu-col-user">
                      {user.username ? <span className="tu-username">{user.username}</span> : <Empty />}
                    </td>
                    <td className="tu-col-email">
                      {user.email ? <span className="tu-email">{user.email}</span> : <Empty />}
                    </td>
                    <td className="tu-col-phone">{user.phone_no ? <span className="tu-phone">{user.phone_no}</span> : <Empty />}</td>
                    <td className="tu-col-role">{renderRole(user)}</td>
                    <td className="tu-col-dept">{user.department_name || <Empty />}</td>
                    <td className="tu-col-status">{renderStatus(user)}</td>
                    <td className="tu-col-actions">{renderActions(user)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="unit-footer tech-footer">
          <div className="unit-count-text">
            Showing <strong>{rangeStart}</strong> to <strong>{rangeEnd}</strong> of <strong>{total}</strong> entries
          </div>

          {totalPages > 1 && (
            <ul className="pagination unit-pagination tech-pagination">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button type="button" className="page-link" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>
                  Prev
                </button>
              </li>
              {compactPageItems(
                [...Array(totalPages)].map((_, idx) => (
                  <li key={idx} className={`page-item ${page === idx + 1 ? "active" : ""}`}>
                    <button type="button" className="page-link" onClick={() => setPage(idx + 1)}>
                      {idx + 1}
                    </button>
                  </li>
                )),
                page
              )}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button type="button" className="page-link" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>
                  Next
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {formMode && (
        <div className="modal show fade d-block unit-modal" tabIndex="-1" style={{ background: "rgba(74, 14, 34, 0.55)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered admin-teal-modal admin-teal-modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <PersonAddAltOutlinedIcon className="modal-header-icon" aria-hidden="true" />
                  {formMode === "create" ? "Add Team User" : "Edit Team User"}
                </h5>
                <button type="button" className="btn-close" onClick={closeForm} title="Close" aria-label="Close"></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-name">Name</label>
                    <input id="tu-name" className="form-control" placeholder="Enter full name" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-username">Username</label>
                    <input
                      id="tu-username"
                      autoComplete="off"
                      className="form-control"
                      placeholder="Enter username"
                      value={form.username}
                      onChange={(e) => handleChange("username", e.target.value)}
                      disabled={formMode === "edit"}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-email">Email</label>
                    <input id="tu-email" className="form-control" placeholder="Enter email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-phone">Phone</label>
                    <input id="tu-phone" type="tel" autoComplete="off" className="form-control" placeholder="Enter phone number" value={form.phone_no} onChange={(e) => handleChange("phone_no", e.target.value)} />
                  </div>

                  {formMode === "create" && (
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="tu-password">Password</label>
                      <div className="input-group">
                        <input
                          id="tu-password"
                          type={showPassword ? "text" : "password"}
                          className="form-control"
                          placeholder="At least 6 characters"
                          autoComplete="new-password"
                          value={form.password}
                          onChange={(e) => handleChange("password", e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          title={showPassword ? "Hide password" : "Show password"}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          aria-pressed={showPassword}
                          onClick={() => setShowPassword((v) => !v)}
                        >
                          <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true"></i>
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-role">Role</label>
                    {editingSelf ? (
                      <input id="tu-role" className="form-control" value={editingSelf.role_name || ""} disabled readOnly />
                    ) : (
                      <select id="tu-role" className="form-select" value={form.role_id} onChange={(e) => handleChange("role_id", e.target.value)}>
                        <option value="">Select Role</option>
                        {options.roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-code">Employee Code</label>
                    <input id="tu-code" className="form-control" placeholder="Optional" value={form.employee_code} onChange={(e) => handleChange("employee_code", e.target.value)} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-unit">Unit</label>
                    <input id="tu-unit" className="form-control" value={managerUnitName} disabled readOnly />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-dept">Department</label>
                    <select
                      id="tu-dept"
                      className="form-select"
                      value={form.department_id}
                      onChange={(e) => setForm((prev) => ({ ...prev, department_id: e.target.value, designation_id: "" }))}
                    >
                      <option value="">Select Department (optional)</option>
                      {departmentOptions.map((d) => renderOrgOption(d))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-desig">Designation</label>
                    <select id="tu-desig" className="form-select" value={form.designation_id} onChange={(e) => handleChange("designation_id", e.target.value)}>
                      <option value="">Select Designation (optional)</option>
                      {designationOptions.map((d) => renderOrgOption(d))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" htmlFor="tu-mgr">Reporting Manager</label>
                    <select id="tu-mgr" className="form-select" value={form.reporting_manager_id} onChange={(e) => handleChange("reporting_manager_id", e.target.value)}>
                      <option value="">No reporting manager (optional)</option>
                      {reportingManagerOptions.map((m) =>
                        renderOrgOption(m, `${m.name}${m.username ? ` (${m.username})` : ""}`)
                      )}
                    </select>
                  </div>

                  {formMode === "edit" && !editingSelf && (
                    <div className="col-md-6">
                      <label className="form-label d-block">Account Status</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="tu-active"
                          checked={!!form.is_active}
                          // Switching off is a deactivation: users:delete_team, same as the backend.
                          disabled={!canDeactivate && !!form.is_active}
                          onChange={(e) => handleChange("is_active", e.target.checked ? 1 : 0)}
                        />
                        <label className="form-check-label" htmlFor="tu-active">
                          {form.is_active ? "Active" : "Inactive"}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <small className="text-muted d-block mt-3">The user is always created in your own unit.</small>
              </div>

              <div className="modal-footer">
                <button className="unit-btn unit-btn-light" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
                <button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : formMode === "create" ? "Create User" : "Update User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewUser && (
        <div className="modal show fade d-block unit-modal" tabIndex="-1" style={{ background: "rgba(74, 14, 34, 0.55)" }}>
          <div className="modal-dialog modal-dialog-centered admin-teal-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">User Details</h5>
                <button type="button" className="btn-close" onClick={() => setViewUser(null)} title="Close" aria-label="Close"></button>
              </div>
              <div className="modal-body">
                {viewLoading ? (
                  <div className="unit-loader">
                    <div className="spinner-border" role="status"></div>
                  </div>
                ) : (
                  <dl className="team-users-detail">
                    {detailRows.map(([label, value]) => (
                      <React.Fragment key={label}>
                        <dt>{label}</dt>
                        <dd>{value || "-"}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}
              </div>
              <div className="modal-footer">
                <button className="unit-btn unit-btn-light" onClick={() => setViewUser(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deactivateTarget}
        onHide={() => setDeactivateTarget(null)}
        onConfirm={confirmDeactivate}
        busy={deactivating}
        title="Deactivate User?"
        message={`${deactivateTarget?.name || "This user"} will no longer be active. You can activate the account again later.`}
        confirmLabel="Deactivate"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
        icon={<DeleteOutlineIcon fontSize="inherit" />}
      />
    </div>
  );
};

export default ManagerTeamUsersPage;
